import React, { useEffect, useState } from 'react';
import Card from '@mui/material/Card';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Skeleton from '@mui/material/Skeleton';
import HandshakeRoundedIcon from '@mui/icons-material/HandshakeRounded';
import PlayerAvatar from './ui/PlayerAvatar';
import api from '../api';
import tokens from '../theme/tokens';

const MEDALHAS = ['🥇', '🥈', '🥉'];
const placar = (p) => `${p.v}V ${p.e}E ${p.d}D`;

/**
 * "Seus melhores parceiros" (Painel e Meu perfil): os 3 companheiros de time com quem quem está logado tem o
 * melhor aproveitamento no ano (mínimo de partidas juntos) e o adversário mais difícil. API: GET /pelada/parcerias.
 */
const ParceirosCard = ({ ano, sx }) => {
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState(false);

  useEffect(() => {
    setDados(null);
    setErro(false);
    api
      .get(`pelada/parcerias${ano ? `?ano=${ano}` : ''}`)
      .then(({ data }) => setDados(data))
      .catch(() => setErro(true));
  }, [ano]);

  if (erro) return null;

  return (
    <Card
      sx={{
        color: '#fff',
        background: `radial-gradient(circle at 100% 0%, rgba(233,168,0,0.28), transparent 55%), linear-gradient(160deg, ${tokens.color.inkSoft}, ${tokens.color.ink})`,
        ...sx,
      }}
    >
      <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
          <HandshakeRoundedIcon sx={{ color: tokens.color.gold }} />
          <Typography sx={{ fontFamily: tokens.font.display, fontWeight: 700, fontSize: '1.4rem', lineHeight: 1.1 }}>
            Seus melhores parceiros{ano ? ` · ${ano}` : ''}
          </Typography>
        </Stack>
        <Typography variant="body2" sx={{ opacity: 0.75, mb: 2 }}>
          Com quem você mais rende no mesmo time (vitória 3 pontos, empate 1).
        </Typography>

        {!dados ? (
          <Stack spacing={1}>{[0, 1, 2].map((i) => <Skeleton key={i} variant="rounded" height={48} sx={{ bgcolor: 'rgba(255,255,255,0.08)' }} />)}</Stack>
        ) : dados.parceiros.length === 0 ? (
          <Typography variant="body2" sx={{ opacity: 0.85, py: 1 }}>
            Ainda não há parceiros: é preciso jogar pelo menos {dados.minimoJogos} partidas no mesmo time com alguém nesta temporada.
          </Typography>
        ) : (
          <Stack spacing={1}>
            {dados.parceiros.map((p, i) => {
              const diferenca = p.aproveitamento - dados.geral.aproveitamento;
              return (
                <Stack key={p.user} direction="row" spacing={1.25} alignItems="center" sx={{ p: 1, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.07)' }}>
                  <Typography sx={{ fontSize: '1.4rem', width: 28, textAlign: 'center' }} aria-hidden>{MEDALHAS[i]}</Typography>
                  <PlayerAvatar src={p.foto} userId={p.user} name={p.nome} size={40} />
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography fontWeight={700} noWrap>{p.nome}</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.75 }}>{p.jogos} jogos juntos · {placar(p)}</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                    <Typography sx={{ fontFamily: tokens.font.display, fontWeight: 700, fontSize: '1.5rem', lineHeight: 1, color: tokens.color.gold }}>
                      {p.aproveitamento}%
                    </Typography>
                    {diferenca !== 0 && (
                      <Typography variant="caption" sx={{ color: diferenca > 0 ? '#86EFAC' : '#FCA5A5', fontWeight: 700 }}>
                        {diferenca > 0 ? '+' : ''}{diferenca} pts
                      </Typography>
                    )}
                  </Box>
                </Stack>
              );
            })}
          </Stack>
        )}

        {dados && (
          <>
            <Typography variant="caption" component="p" sx={{ opacity: 0.75, mt: 1.5 }}>
              Seu aproveitamento geral: <b>{dados.geral.aproveitamento}%</b> em {dados.geral.jogos} partidas ({placar(dados.geral)}). Mínimo de {dados.minimoJogos} jogos juntos.
            </Typography>
            {dados.adversarioDificil && (
              <>
                <Divider sx={{ my: 1.5, borderColor: 'rgba(255,255,255,0.12)' }} />
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <Typography sx={{ fontSize: '1.3rem', width: 28, textAlign: 'center' }} aria-hidden>😤</Typography>
                  <PlayerAvatar src={dados.adversarioDificil.foto} userId={dados.adversarioDificil.user} name={dados.adversarioDificil.nome} size={36} />
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography variant="caption" sx={{ opacity: 0.75, display: 'block' }}>Adversário mais difícil</Typography>
                    <Typography fontWeight={700} noWrap>{dados.adversarioDificil.nome}</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.75 }}>{dados.adversarioDificil.jogos} jogos contra · {placar(dados.adversarioDificil)}</Typography>
                  </Box>
                  <Typography sx={{ fontFamily: tokens.font.display, fontWeight: 700, fontSize: '1.3rem', color: '#FCA5A5', flexShrink: 0 }}>
                    {dados.adversarioDificil.aproveitamento}%
                  </Typography>
                </Stack>
              </>
            )}
          </>
        )}
      </Box>
    </Card>
  );
};

export default ParceirosCard;
